import React, { useState, useEffect, useRef } from "react";
import {
  createTournament,
  fetchTournamentsList,
  getTournamentLookups,
  updateTournament,
} from "../../../api/tournamentControlApi";
import { getCsrfToken } from "../../../../../public/api/authApi";
import {
  GlobalButton,
  GlobalDateField,
  GlobalSelectField,
  GlobalTextAreaField,
  GlobalTextField,
  GlobalContactNumberField,
} from "../../../../../global/forms/GlobalFormFields";
import GlobalFormShell from "../../../../../global/forms/GlobalFormShell";
import {
  sanitizeTournamentNameInput,
  tomorrowYmd,
  registrationLastDateMaxYmd,
  validateTournamentForm,
  normalizeOrganizerNameInput,
  findDuplicateTournamentInList,
  dateInputYmdFromValue,
  TOURNAMENT_NAME_MAX_LEN,
  TOURNAMENT_LOCATION_MAX_LEN,
  ORGANIZER_NAME_MAX_LEN,
  TOURNAMENT_DESCRIPTION_MAX_LEN,
} from "../../../utils/tournamentFormUtils";

const EMPTY = {
  tournament_name: "",
  sport_id: "",
  tournament_level_id: "",
  tournament_date: "",
  tournament_time: "",
  tournament_location: "",
  registration_last_date: "",
  organizer_name: "",
  contact_number: "",
  participation_fee: "",
  max_students_allowed: "",
  tournament_update_id: "",
  description: "",
};

function timeInputFromRow(t) {
  if (t == null || t === "") return "";
  const s = typeof t === "string" ? t : String(t);
  if (s.length >= 5 && s.includes(":")) return s.slice(0, 5);
  return s;
}

function rowToForm(t) {
  return {
    tournament_name: t.tournament_name || "",
    sport_id: t.sport_id != null && t.sport_id !== "" ? String(t.sport_id) : "",
    tournament_level_id:
      t.tournament_level_id != null && t.tournament_level_id !== ""
        ? String(t.tournament_level_id)
        : "",
    tournament_date: dateInputYmdFromValue(t.tournament_date),
    tournament_time: timeInputFromRow(t.tournament_time),
    tournament_location: t.tournament_location || "",
    registration_last_date: dateInputYmdFromValue(t.registration_last_date),
    organizer_name: t.organizer_name || "",
    contact_number:
      t.contact_number != null && t.contact_number !== "" ? String(t.contact_number).replace(/\D/g, "") : "",
    participation_fee:
      t.participation_fee != null && t.participation_fee !== "" ? String(Number(t.participation_fee)) : "",
    max_students_allowed:
      t.max_students_allowed != null && t.max_students_allowed !== "" ? String(t.max_students_allowed) : "",
    tournament_update_id:
      t.tournament_update_id != null && t.tournament_update_id !== ""
        ? String(t.tournament_update_id)
        : "",
    description: t.description || "",
  };
}

function payloadFromForm(formData) {
  const orgTrim = normalizeOrganizerNameInput(formData.organizer_name).trim();
  return {
    tournament_name: sanitizeTournamentNameInput(formData.tournament_name).trim(),
    sport_id: Number(formData.sport_id),
    tournament_level_id: Number(formData.tournament_level_id),
    tournament_date: formData.tournament_date,
    tournament_time: formData.tournament_time || null,
    tournament_location: formData.tournament_location.trim(),
    registration_last_date: formData.registration_last_date || null,
    organizer_name: orgTrim === "" ? null : orgTrim,
    contact_number:
      formData.contact_number && String(formData.contact_number).replace(/\D/g, "").length > 0
        ? String(formData.contact_number).replace(/\D/g, "")
        : null,
    participation_fee: Number(formData.participation_fee),
    max_students_allowed: parseInt(String(formData.max_students_allowed), 10),
    tournament_update_id: Number(formData.tournament_update_id),
    description: formData.description?.trim() ? formData.description.trim() : null,
  };
}

function TournamentCreationForm({ onSuccess, onCancelEdit, editingTournament = null, lookupsRefreshKey = 0 }) {
  const isEdit = Boolean(editingTournament?.id);
  const [formData, setFormData] = useState(EMPTY);
  const [lookups, setLookups] = useState({
    sports: [],
    tournamentLevels: [],
    tournamentUpdates: [],
    tournamentResultTypes: [],
  });
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const alertRef = useRef(null);

  useEffect(() => {
    if (!editingTournament?.id) {
      setFormData({ ...EMPTY });
      setError(null);
      setErrorDetails({});
      return;
    }
    setFormData(rowToForm(editingTournament));
    setError(null);
    setErrorDetails({});
  }, [editingTournament]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getTournamentLookups();
        if (cancelled || !res?.success || !res?.data) return;
        setLookups({
          sports: res.data.sports || [],
          tournamentLevels: res.data.tournamentLevels || [],
          tournamentUpdates: res.data.tournamentUpdates || [],
          tournamentResultTypes: res.data.tournamentResultTypes || [],
        });
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lookupsRefreshKey]);

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errorDetails[name]) setErrorDetails((d) => ({ ...d, [name]: "" }));
    setError(null);
  };

  const regMinYmd = tomorrowYmd();
  const regMaxRaw = registrationLastDateMaxYmd(formData.tournament_date);
  const registrationDateMax =
    regMaxRaw && String(regMaxRaw) >= String(regMinYmd) ? regMaxRaw : undefined;

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    const clientErr = validateTournamentForm(formData, { isEdit });
    if (Object.keys(clientErr).length > 0) {
      setErrorDetails(clientErr);
      setError("Please fix the highlighted fields.");
      setTimeout(() => alertRef.current?.focus(), 0);
      return;
    }

    const payloadDup = payloadFromForm(formData);
    try {
      const listRes = await fetchTournamentsList({});
      const dup = findDuplicateTournamentInList(payloadDup, listRes?.data || [], {
        excludeId: isEdit ? editingTournament.id : undefined,
      });
      if (dup) {
        setError(
          "Duplicate tournament: an active tournament already has the same name, location, and time.",
        );
        setTimeout(() => alertRef.current?.focus(), 0);
        return;
      }
    } catch {
      /* list failed; server still enforces duplicate rule */
    }

    setLoading(true);
    setError(null);
    setErrorDetails({});
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      const payload = payloadFromForm(formData);
      if (isEdit) {
        await updateTournament(editingTournament.id, payload, csrfToken);
        onSuccess?.("Tournament updated successfully.");
        onCancelEdit?.();
      } else {
        await createTournament(payload, csrfToken);
        onSuccess?.("Tournament created successfully.");
        setFormData({ ...EMPTY });
      }
    } catch (err) {
      const msg = err?.message || "Request failed";
      setError(msg);
      if (err.errors && typeof err.errors === "object") setErrorDetails(err.errors);
      setTimeout(() => alertRef.current?.focus(), 0);
    } finally {
      setLoading(false);
    }
  };

  const ALERT_ID = "create-tournament-alert";

  const sportOpts = lookups.sports.map((s) => ({
    value: s.id,
    label: s.sport_name,
  }));
  const levelOpts = lookups.tournamentLevels.map((x) => ({
    value: x.id,
    label: x.level_name,
  }));
  const updateOpts = lookups.tournamentUpdates.map((x) => ({
    value: x.id,
    label: x.update_name,
  }));

  return (
    <GlobalFormShell
      legend={isEdit ? "Edit tournament" : "Add tournament"}
      showAlert={!!error}
      alertText={error}
      alertId={ALERT_ID}
      formAlertRef={alertRef}
      actions={
        <>
          {isEdit && (
            <GlobalButton type="button" variant="ghost" onClick={() => onCancelEdit?.()} disabled={loading}>
              Cancel edit
            </GlobalButton>
          )}
          <GlobalButton type="button" variant="primary" loading={loading} onClick={handleSubmit}>
            {isEdit ? "Save changes" : "Create tournament"}
          </GlobalButton>
        </>
      }
    >
      {isEdit && editingTournament?.tournament_code ? (
        <p
          className="gff-field gff-field--full"
          style={{ margin: "0 0 0.25rem 0", fontSize: "0.8125rem", color: "#475569" }}
        >
          <span className="gff-label" style={{ display: "block", marginBottom: "0.25rem" }}>
            Tournament code (automatic)
          </span>
          <strong style={{ letterSpacing: "0.02em" }}>{editingTournament.tournament_code}</strong>
        </p>
      ) : null}

      <GlobalTextField
        id="tournament_name"
        label="Tournament name"
        value={formData.tournament_name}
        onChange={(v) => setField("tournament_name", sanitizeTournamentNameInput(v))}
        onBlur={() =>
          setFormData((prev) => ({
            ...prev,
            tournament_name: String(prev.tournament_name || "").trim(),
          }))
        }
        error={errorDetails.tournament_name}
        required
        maxLength={TOURNAMENT_NAME_MAX_LEN}
      />
      <GlobalSelectField
        id="sport_id"
        label="Sport"
        value={formData.sport_id}
        onChange={(v) => setField("sport_id", v)}
        options={sportOpts}
        placeholderOption="Select sport"
        error={errorDetails.sport_id}
        required
      />
      <GlobalSelectField
        id="tournament_level_id"
        label="Tournament level"
        value={formData.tournament_level_id}
        onChange={(v) => setField("tournament_level_id", v)}
        options={levelOpts}
        placeholderOption="Select level"
        error={errorDetails.tournament_level_id}
        required
      />
      <GlobalSelectField
        id="tournament_update_id"
        label="Tournament update"
        value={formData.tournament_update_id}
        onChange={(v) => setField("tournament_update_id", v)}
        options={updateOpts}
        placeholderOption="Select tournament update"
        error={errorDetails.tournament_update_id}
        required
      />
      <GlobalDateField
        id="tournament_date"
        label="Tournament date"
        value={formData.tournament_date}
        onChange={(v) => setField("tournament_date", v)}
        error={errorDetails.tournament_date}
        min={isEdit ? undefined : tomorrowYmd()}
        required
      />
      <GlobalTextField
        id="tournament_time"
        label="Tournament time"
        type="time"
        value={formData.tournament_time}
        onChange={(v) => setField("tournament_time", v)}
        error={errorDetails.tournament_time}
        required
      />
      <GlobalTextField
        id="tournament_location"
        label="Location"
        value={formData.tournament_location}
        onChange={(v) => setField("tournament_location", v)}
        error={errorDetails.tournament_location}
        required
        maxLength={TOURNAMENT_LOCATION_MAX_LEN}
      />
      <GlobalDateField
        id="registration_last_date"
        label="Registration last date"
        value={formData.registration_last_date}
        onChange={(v) => setField("registration_last_date", v)}
        error={errorDetails.registration_last_date}
        min={regMinYmd}
        max={registrationDateMax}
        required
      />
      <GlobalTextField
        id="organizer_name"
        label="Organizer name (optional)"
        value={formData.organizer_name}
        onChange={(v) => setField("organizer_name", normalizeOrganizerNameInput(v))}
        onBlur={() =>
          setFormData((prev) => ({
            ...prev,
            organizer_name: normalizeOrganizerNameInput(prev.organizer_name).trim(),
          }))
        }
        error={errorDetails.organizer_name}
        maxLength={ORGANIZER_NAME_MAX_LEN}
      />
      <GlobalContactNumberField
        id="organizer_contact_number"
        label="Organizer contact number"
        value={formData.contact_number}
        onChange={(v) => setField("contact_number", v)}
        error={errorDetails.contact_number}
      />
      <GlobalTextField
        id="participation_fee"
        label="Participation fee"
        type="number"
        min="0"
        step="0.01"
        value={formData.participation_fee}
        onChange={(v) => setField("participation_fee", v)}
        error={errorDetails.participation_fee}
        required
      />
      <GlobalTextField
        id="max_students_allowed"
        label="Maximum students"
        type="number"
        min="2"
        step="1"
        value={formData.max_students_allowed}
        onChange={(v) => setField("max_students_allowed", v)}
        error={errorDetails.max_students_allowed}
        required
      />
      <GlobalTextAreaField
        id="description"
        label="Description"
        value={formData.description}
        onChange={(v) =>
          setField("description", String(v ?? "").slice(0, TOURNAMENT_DESCRIPTION_MAX_LEN))
        }
        rows={4}
        classNames={{ fieldWrap: "gff-field gff-field--full" }}
        error={errorDetails.description}
      />

      {isEdit && (
        <div
          className="gff-field--full"
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
            padding: "0.9rem 1rem",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "0.65rem",
            }}
          >
            <strong style={{ fontSize: "0.9rem", color: "#0f172a", letterSpacing: "0.01em" }}>
              Tournament totals
            </strong>
            <span
              style={{
                fontSize: "0.73rem",
                color: "#475569",
                background: "#e2e8f0",
                borderRadius: "999px",
                padding: "0.18rem 0.55rem",
                fontWeight: 600,
              }}
            >
              Read only
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "0.55rem" }}>
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                background: "#ffffff",
                padding: "0.6rem 0.7rem",
              }}
            >
              <div style={{ fontSize: "0.76rem", color: "#64748b", marginBottom: "0.2rem" }}>
                Registered students
              </div>
              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>
                {editingTournament.total_registered ?? 0}
              </div>
            </div>
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                background: "#ffffff",
                padding: "0.6rem 0.7rem",
              }}
            >
              <div style={{ fontSize: "0.76rem", color: "#64748b", marginBottom: "0.2rem" }}>Collected fee</div>
              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>
                ₹{Number(editingTournament.total_collected_fee || 0).toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      )}
    </GlobalFormShell>
  );
}

export default function TournamentCreateSection({
  lookupsRefreshKey,
  editingTournament,
  onCancelEdit,
  onSuccess,
}) {
  return (
    <div id="td-create" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
      <div className="user-builder-panel__head">
        <h3 className="user-builder-panel__title">Tournament creation</h3>
        <p className="user-builder-panel__hint">
          Add a new tournament or choose <b>Edit</b> in the directory below. Boolean fields default off unless you
          enable them.
        </p>
      </div>
      <TournamentCreationForm
        lookupsRefreshKey={lookupsRefreshKey}
        editingTournament={editingTournament}
        onCancelEdit={onCancelEdit}
        onSuccess={onSuccess}
      />
    </div>
  );
}
